import { MD5 } from 'crypto-js';
import { useEffect, useRef, useState } from 'react';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import { ButtonPrimary } from '../../../../utils/Button';
import FocusControl from '../../../../utils/FocusControl';
import { TextareaField } from '../../../../utils/InputField';
import { CrossIcon, FolderOpenIcon, ImageUploadingIcon } from '../../../../utils/SVGIcon';
import { GetFileSize, GetFileTypeToFull } from '../../../../utils/Show';
import cn from '../../../../utils/cn';
import {
    ApiSendSupportMessage,
    ApiUploadSupportTicketAttachment
} from '../../SupportTicketOperation';
import { useTicketInfo } from '../SupportTicketInfoProvider';

// Uploading  Temp Loading
function UploadingTempLoading({ handleFileChange, isLoading }) {
    return (
        <label
            htmlFor="attachmentFile"
            className={cn(
                'flex justify-between items-center gap-2 bg-secondary-200 px-2 rounded-md text-secondary cursor-pointer relative',
                {
                    'pointer-events-none select-none bg-primary-200 text-gray-400': isLoading
                }
            )}
        >
            +
            {isLoading && (
                <div className="size-22 text-start bg-transparent text-secondary rounded-md absolute z-50 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ">
                    <ImageUploadingIcon />
                </div>
            )}
            <input
                id="attachmentFile"
                type="file"
                className="w-full h-12 border border-gray-300 rounded-md px-4 hidden"
                onChange={handleFileChange}
                accept="image/*, .pdf, .doc, .docx, .txt, .jpg, .jpeg, .png, .mp4, .avi, .wmv, .mov, .3gp, .3g2, .ogg, .webm, .mp3, .wav, .mp4"
                data-max-files="5"
            />
        </label>
    );
}

function FileAttachment({ formData, setFormData, handleSend }) {
    const [tempAttachment, setTempAttachment] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const { Attachment } = formData;
    const [error, setError] = useState('');

    useEffect(() => {
        if (Attachment?.length === 0) {
            setTempAttachment([]);
        }
    }, [Attachment.length, formData]);

    // Handle File Change and Upload
    const handleFileChange = (e) => {
        // Check total file size
        if (Attachment.length >= 5) {
            setError('You can upload maximum 5 files');
            return;
        }
        // Data Destructuring
        const { files } = e.target;
        // Check total selected files error handling
        if (files.length === 0) return;

        // check same file name
        const isSameFile = tempAttachment?.some((item) => item.name === files[0].name);
        if (isSameFile) {
            setError('You have already selected this file');
            return;
        }
        // only file upload 500bytes to 100MB
        if (files?.[0] && files[0].size <= 500) {
            setError('File size too small, minimum file size 500 bytes');
            return;
        }
        if (files?.[0] && files[0].size >= 100000000) {
            setError('File size too large, maximum file size 100MB');
            return;
        }
        const temFile = new FormData();
        temFile.append('attachment', files[0]);
        setIsUploading(true);
        ApiUploadSupportTicketAttachment(temFile, setError)
            .then(({ data }) => {
                setIsUploading(false);
                setFormData((d) => ({
                    ...d,
                    Attachment: [...d.Attachment, data.fileUrl]
                }));
                setTempAttachment((d) => [...d, files[0]]);
            })
            .catch((err) => {
                setError(err.toString(0));
                setIsUploading(false);
            });
    };
    // Remove File from tempAttachment and Attachment
    const removeFile = (indexToRemove) => {
        setFormData((prevFormData) => {
            const updatedAttachment = prevFormData.Attachment.filter(
                (_, index) => index !== indexToRemove
            );
            return { ...prevFormData, Attachment: updatedAttachment };
        });

        setTempAttachment((prevTempAttachment) => {
            const updatedTempAttachment = prevTempAttachment.filter(
                (_, index) => index !== indexToRemove
            );
            return updatedTempAttachment;
        });
    };
    // Get File from Drag over file
    const handleDragOver = (e) => {
        e?.preventDefault();
    };
    // Get File from Drop file and upload
    const handleDrop = (e) => {
        e?.preventDefault();
        const { files } = e.dataTransfer;
        handleFileChange({ target: { files } });
    };
    // 3 sec error message hide
    useEffect(() => {
        if (error) {
            setTimeout(() => {
                setError('');
            }, 5000);
        }
    }, [error]);

    return (
        <div className="flex items-start justify-between gap-3">
            <div>
                <div>
                    <div className="flex gap-x-2 mb-2">
                        <p className="pl-2 text-base capitalize h-auto font-normal">
                            Attachments <span className="">(Optional)</span>
                        </p>

                        <UploadingTempLoading
                            handleFileChange={handleFileChange}
                            isLoading={isUploading}
                        />
                    </div>

                    {tempAttachment && tempAttachment?.length ? (
                        <div
                            className="flex flex-wrap gap-2 border border-primary-200 px-2 py-3 mb-2 rounded-lg transition-all"
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            {tempAttachment?.map((item, indexToRemove) => {
                                return (
                                    <div
                                        key={MD5(item + indexToRemove)}
                                        className="flex justify-between items-center gap-2 bg-secondary-100 px-2 py-1 rounded-md"
                                    >
                                        <div className="flex justify-start items-center gap-2 font-medium text-primary-700">
                                            <p className="w-3/6 text-sm line-clamp-1">
                                                {item?.name}
                                            </p>
                                            <span className="text-xs leading-5 text-primary-700/70 whitespace-nowrap">
                                                {GetFileSize(item?.size)}
                                            </span>
                                            <span className="text-xs text-primary-700/70 whitespace-nowrap hidden sm:block">
                                                {GetFileTypeToFull(item?.type)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {/* View Attachment */}
                                            <button
                                                type="button"
                                                className="text-sm size-7 p-1 text-primary hidden sm:block"
                                                onClick={() => {
                                                    const url = URL.createObjectURL(item);
                                                    window.open(url, '_blank');
                                                }}
                                            >
                                                <FolderOpenIcon />
                                            </button>
                                            {/* Remove attachment */}
                                            <button
                                                type="button"
                                                className="text-error"
                                                onClick={() => removeFile(indexToRemove)}
                                            >
                                                <CrossIcon />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                <p className="text-sm text-error px-2 mb-1">{error}</p>
            </div>

            <div className="flex justify-end">
                <ButtonPrimary title="Send" onClick={handleSend} />
            </div>
        </div>
    );
}

function ConversationNotFoundView({ show }) {
    if (!show) return null;
    return (
        <div className="text-center py-4">
            <span className="text-gray-400">No conversation found!</span>
        </div>
    );
}

function ConversationInputArea({ data, handleReload }) {
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const { setMessage } = useTemplate();
    const { setFormData, handleChange, formData } = useTicketInfo();
    const [isError, setIsError] = useState('');
    const payLoad = {
        ...formData,
        SupportTicketId: data?.id
    };

    const handleSend = (e) => {
        setLoading(true);
        e.preventDefault();
        ApiSendSupportMessage(data?.id, payLoad)
            .then(() => {
                handleReload();
                setLoading(false);
                setFormData({ SupportTicketId: 0, Message: '', Attachment: [] });
                // setMessage({ type: 'success', text: title });
            })
            .catch((error) => {
                setLoading(false);
                setMessage({ type: 'error', text: error.toString() });
            });
    };

    useEffect(() => {
        if (data && data?.status === 'closed') {
            inputRef.current.blur();
        } else {
            inputRef.current.focus();
        }
    });

    useEffect(() => {
        if (data && data?.status !== 'closed') {
            inputRef.current.focus();
        }
    }, [data, data.status, loading]); // Include your condition in the dependency array

    FocusControl(isError, setIsError, formData);
    return (
        <div className="pr-1 mb-4">
            <ConversationNotFoundView show={data?.messages && data?.messages?.length === 0} />

            <form
                className={cn({
                    'grayscale cursor-default select-none pointer-events-none':
                        data?.demo || data?.status === 'closed' || loading
                })}
            >
                <div className="mb-2">
                    <TextareaField
                        name="Message"
                        placeholder="Type your message here...."
                        formData={formData}
                        onChange={handleChange}
                        inputRef={inputRef}
                        disabled={loading || data?.demo || data?.status === 'closed'}
                        className="h-52"
                    />
                </div>

                <FileAttachment
                    formData={formData}
                    setFormData={setFormData}
                    handleSend={handleSend}
                />
            </form>
        </div>
    );
}

export default ConversationInputArea;
